import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ListTodo, Building2, ChevronDown, ChevronRight } from "lucide-react";
import { TaskCard } from "@/components/tasks/TaskCard";

const NO_CUSTOMER = "__no_customer__";

export function TasksByCustomer({ tasks = [], onEdit, onDelete, onStatusChange, myDayIds, onToggleMyDay, onPlanWeek }) {
  const groups = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      const key = task.customer_name ? task.customer_name : NO_CUSTOMER;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(task);
    });

    const entries = Array.from(map.entries()).map(([name, items]) => ({
      name,
      label: name === NO_CUSTOMER ? "Ohne Kunde" : name,
      tasks: items,
      openCount: items.filter((t) => t.status !== "Erledigt").length,
    }));

    // Sort alphabetically; "Ohne Kunde" always last
    entries.sort((a, b) => {
      if (a.name === NO_CUSTOMER) return 1;
      if (b.name === NO_CUSTOMER) return -1;
      return a.label.localeCompare(b.label, "de");
    });

    return entries;
  }, [tasks]);

  const [collapsed, setCollapsed] = useState({});

  const toggle = (name) =>
    setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));

  if (tasks.length === 0) {
    return (
      <Card className="border-dashed" data-testid="tasks-by-customer-empty">
        <CardContent className="py-12 text-center text-muted-foreground">
          <ListTodo className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Keine Aufgaben gefunden</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="tasks-by-customer">
      {groups.map((group) => {
        const isCollapsed = !!collapsed[group.name];
        return (
          <div
            key={group.name}
            className="rounded-lg border bg-card"
            data-testid={`customer-group-${group.name === NO_CUSTOMER ? "none" : group.name}`}
          >
            <button
              type="button"
              onClick={() => toggle(group.name)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors rounded-t-lg"
              data-testid={`customer-group-toggle-${group.name === NO_CUSTOMER ? "none" : group.name}`}
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
              <Building2 className="w-4 h-4 text-primary" />
              <span className="font-semibold text-left flex-1 truncate">{group.label}</span>
              {group.openCount > 0 && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {group.openCount} offen
                </Badge>
              )}
              <Badge variant="secondary">{group.tasks.length}</Badge>
            </button>

            {!isCollapsed && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4 pt-0">
                {group.tasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onStatusChange={onStatusChange}
                    isInMyDay={myDayIds?.has(task.id)}
                    onToggleMyDay={onToggleMyDay}
                    onPlanWeek={onPlanWeek}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
